/**
 * Legacy schedule duration (old system shift-scheduled + schedule day/client models).
 * - Gross minutes = end − start
 * - Break = 30m when unpaidBreak / unpaid_break flag is set, else numeric unpaid_break minutes
 * - Net work minutes = gross − break
 */

function shiftStartEndRaw(shiftLike) {
  if (!shiftLike) return { start: null, end: null };
  const date =
    shiftLike.shift_date ??
    shiftLike.shiftDate ??
    (shiftLike.start_date
      ? String(shiftLike.start_date).slice(0, 10)
      : null);
  const start =
    shiftLike.start_date ??
    shiftLike.startDate ??
    (date && shiftLike.scheduledStart
      ? `${date}T${shiftLike.scheduledStart}`
      : shiftLike.scheduledStart ?? null);
  const end =
    shiftLike.end_date ??
    shiftLike.endDate ??
    (date && shiftLike.scheduledEnd
      ? `${date}T${shiftLike.scheduledEnd}`
      : shiftLike.scheduledEnd ?? null);
  return { start, end };
}

/** Legacy: unpaidBreak ? 30 : numeric unpaid_break minutes (or 0). */
export function resolveLegacyUnpaidBreakMinutes(shiftLike) {
  if (!shiftLike) return 0;
  if (
    shiftLike.unpaidBreak === true ||
    shiftLike.unpaidBreak === 1 ||
    shiftLike.unpaidBreak === "1" ||
    shiftLike.unpaid_break === true ||
    shiftLike.unpaid_break === 1 ||
    shiftLike.unpaid_break === "1"
  ) {
    return 30;
  }
  const n = Number(
    shiftLike.unpaid_break ??
      shiftLike.unpaidBreak ??
      shiftLike.unpaidBreakMinutes,
  );
  if (Number.isFinite(n) && n > 0) return n;
  return 0;
}

/** Scheduled span in minutes (before break), from dates or durationMinutes. */
export function shiftGrossDurationMinutes(shiftLike) {
  if (!shiftLike) return 0;
  const dm = Number(shiftLike.durationMinutes ?? shiftLike.duration_minutes);
  if (Number.isFinite(dm) && dm > 0) return dm;

  const { start, end } = shiftStartEndRaw(shiftLike);
  if (start && end) {
    const startMs = new Date(start).getTime();
    const endMs = new Date(end).getTime();
    if (Number.isFinite(startMs) && Number.isFinite(endMs)) {
      return Math.max(0, Math.round((endMs - startMs) / 60000));
    }
  }

  const netHours = Number(
    shiftLike.scheduledHours ?? shiftLike.scheduled_hours ?? shiftLike.duration_hours,
  );
  if (Number.isFinite(netHours) && netHours >= 0) {
    const brk = resolveLegacyUnpaidBreakMinutes(shiftLike);
    return Math.round(netHours * 60 + brk);
  }
  return 0;
}

export function shiftNetWorkMinutes(shiftLike) {
  const gross = shiftGrossDurationMinutes(shiftLike);
  const brk = resolveLegacyUnpaidBreakMinutes(shiftLike);
  return Math.max(0, gross - brk);
}

export function shiftNetWorkHours(shiftLike) {
  return Math.round((shiftNetWorkMinutes(shiftLike) / 60) * 100) / 100;
}

function formatMinutesParts(totalMinutes) {
  const minutes = Math.max(0, Math.round(Number(totalMinutes) || 0));
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const hoursPart = hours > 0 ? `${hours}h` : "";
  const minsPart = mins > 0 ? `${mins}m` : "";
  return [hoursPart, minsPart].filter(Boolean).join(" ") || "0h";
}

/** e.g. "7h 30m after 30m break" (ShiftDetailPanel / legacy scheduled component). */
export function formatLegacyScheduleDurationText(shiftLike) {
  const netMin = shiftNetWorkMinutes(shiftLike);
  const brk = resolveLegacyUnpaidBreakMinutes(shiftLike);
  const durationText = formatMinutesParts(netMin);
  if (!brk) return durationText;
  const breakText = formatMinutesParts(brk);
  return `${durationText} after ${breakText} break`;
}

/** Schedule board hour chips (day / client / slot totals). */
export function formatLegacyScheduleHoursLabel(hoursNum) {
  const n = Number(hoursNum);
  if (!Number.isFinite(n) || n <= 0) return "0h";
  if (n % 1 === 0) return `${n}h`;
  return `${Number(n).toFixed(1)}h`;
}

/** Legacy schedule: hours count for Unassigned / Tentative / Confirmed only. */
export function shiftCountsTowardScheduleHours(shiftStatusRaw, displayStatus) {
  const st = String(shiftStatusRaw ?? "").trim().toLowerCase();
  const disp = String(displayStatus ?? "").trim();
  if (st === "cancelled" || st === "declined") return false;
  if (disp === "Cancelled" || disp === "Unfilled") return false;
  return true;
}

export function sumScheduleGroupHours(groups) {
  return (groups || []).reduce(
    (sum, g) => sum + (Number(g.totalHours) || 0),
    0,
  );
}
